import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import apiAxios from "helpers/apiAxios";
import { addRejectedMatcher, handleAsyncThunkError } from "redux/reduxHelpers";

const projectUrl = '/crm/projects';

// Hàng trong state.responses có thể là JSON:API ({ id: "12", attributes }) khi
// tải từ server, hoặc phẳng khi vừa tạo — id ngoài là chuỗi, id trong attributes
// là số, nên luôn so sánh qua String().
const responseAttrs = (row) => row?.attributes || row;
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

// Response vừa tạo được chèn lên đầu trang 1, đẩy một response cũ sang trang 2 —
// "Load more" sẽ trả lại chính nó. Bỏ bản trùng để không có hai thread cùng key.
export const appendPage = (existing, rows) => {
  const seen = new Set(existing.map((row) => String(responseAttrs(row)?.id)));
  return [...existing, ...rows.filter((row) => !seen.has(String(responseAttrs(row)?.id)))];
};

/**
 * Đưa response vừa tạo vào danh sách của topic mà không refetch.
 *   - response gốc: lên đầu danh sách (server sắp mới → cũ), total_rows + 1
 *   - reply (có parent_id): nối vào cuối replies của gốc (cũ → mới). total_rows
 *     KHÔNG đổi vì nó đếm response gốc để phân trang.
 * Gốc chưa có trong trang đang tải thì bỏ qua — lần tải sau sẽ có.
 */
export const withCreatedResponse = (topicResponses, created) => {
  if (!created) return topicResponses;

  if (created.parent_id) {
    return {
      ...topicResponses,
      data: topicResponses.data.map((root) => {
        const attrs = responseAttrs(root);
        if (!sameId(attrs?.id, created.parent_id)) return root;

        const replies = [...(attrs.replies || []), created];
        return root.attributes ? { ...root, attributes: { ...attrs, replies } } : { ...root, replies };
      }),
    };
  }

  return {
    ...topicResponses,
    data: [{ ...created, replies: created.replies || [] }, ...topicResponses.data],
    paginate: {
      ...topicResponses.paginate,
      total_rows: (topicResponses.paginate?.total_rows || 0) + 1,
    },
  };
};

// Async thunks
export const getTopics = createAsyncThunk(
  'crm_project_interaction/getTopics',
  async ({ projectId, params }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/${projectId}/interactions?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get topics'));
    }
  }
);

export const getTopicsWithoutProject = createAsyncThunk(
  'crm_project_interaction/getTopicsWithoutProject',
  async ({ params }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/interactions?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get topics without project'));
    }
  }
);

export const createTopic = createAsyncThunk(
  'crm_project_interaction/createTopic',
  async ({ projectId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/interactions`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create topic'));
    }
  }
);

export const updateTopic = createAsyncThunk(
  'crm_project_interaction/updateTopic',
  async ({ projectId, topicId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${projectUrl}/${projectId}/interactions/${topicId}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to update topic'));
    }
  }
);

export const deleteTopic = createAsyncThunk(
  'crm_project_interaction/deleteTopic',
  async ({ projectId, topicId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.delete(`${projectUrl}/${projectId}/interactions/${topicId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete topic'));
    }
  }
);

export const closeTopic = createAsyncThunk(
  'crm_project_interaction/closeTopic',
  async ({ projectId, topicId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/interactions/${topicId}/close`);
      return response.data;
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to close topic'));
    }
  }
);

export const getTopicResponses = createAsyncThunk(
  'crm_project_interaction/getTopicResponses',
  async ({ projectId, topicId, params }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/${projectId}/interactions/${topicId}/responses?${params}`);
      return { topicId, ...response.data };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to get topic responses'));
    }
  }
);

export const createResponse = createAsyncThunk(
  'crm_project_interaction/createResponse',
  async ({ projectId, topicId, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/interactions/${topicId}/responses`, data, {
        headers: { 'Content-Type': 'multipart/form-data'}
      })

      return { topicId, response: response.data };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to create response'));
    }
  }
);

export const deleteResponse = createAsyncThunk(
  'crm_project_interaction/deleteResponse',
  async ({ projectId, topicId, responseId,  }, { rejectWithValue }) => {
    try {
      await apiAxios.delete(`${projectUrl}/${projectId}/interactions/${topicId}/responses/${responseId}`);
      return { topicId, responseId };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to delete response'));
    }
  }
);

export const likeResponse = createAsyncThunk(
  'crm_project_interaction/likeResponse',
  async ({ projectId, responseId, topicId }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${projectUrl}/${projectId}/responses/${responseId}/like`);
      return { topicId, responseId, likes: response.data.likes };
    } catch (error) {
      return rejectWithValue(handleAsyncThunkError(error, 'Failed to like response'));
    }
  }
);

export const topicExportExcel = createAsyncThunk(
  'crm_project_interaction/topicExportExcel',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiAxios.get(`${projectUrl}/interactions_export_to_excel?${params}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `interactions_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Price matrix exported successfully');
      return true;
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export price matrix');
      return rejectWithValue(error.message);
    }
  }
);

// Initial state
const initialState = {
  data: [],
  responses: [],
  totalRows: 0,
  loading: false,
  responseLoading: [],
  error: null,
  paginate: {
    total_rows: 0,
    current_page: 1,
    prev_page: null,
    next_page: null,
    per_page: 30,
    total_pages: 1
  },
  // currentParams: {},
  // sortParams: {
  //   sortBy: 'created_at',
  //   sortOrder: 'desc'
  // },
  // filters: {}
};

// Slice
const interactionSlice = createSlice({
  name: 'crm_project_interaction',
  initialState,
  reducers: {
    // setFilters: (state, action) => {
    //   state.filters = { ...state.filters, ...action.payload };
    // },
    // setSortParams: (state, action) => {
    //   state.sortParams = { ...state.sortParams, ...action.payload };
    // },
    setCurrentParams: (state, action) => {
      state.currentParams = action.payload || {};
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => {
      return { ...initialState };
    },
    onToggleResponses: (state, action) => {
      const topicId = action.payload;
      
      if (!state.responses[topicId]) {
        // Create new responses object for this topic
        state.responses = {
          ...state.responses,
          [topicId]: {
            data: [],
            visible: true,
            loaded: false,
            paginate: {
              total_rows: 0,
              current_page: 1,
              prev_page: null,
              next_page: null,
              per_page: 5,
              total_pages: 1
            }
          }
        };
      } else {
        // Toggle visibility for existing topic
        state.responses = {
          ...state.responses,
          [topicId]: {
            ...state.responses[topicId],
            visible: !state.responses[topicId].visible
          }
        };
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Get Topics
      .addCase(getTopics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTopics.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.data = Array.isArray(payload.rows) ? payload.rows.map(item => item.attributes) : [];
        state.totalRows = payload.total_rows || 0;
        state.paginate = payload.paginate || initialState.paginate;
      })
      .addCase(getTopics.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload || 'Failed to fetch topics';
        state.data = [];
      })

      .addCase(getTopicsWithoutProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTopicsWithoutProject.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        state.data = Array.isArray(payload.rows) ? payload.rows.map(item => item.attributes) : [];
        state.totalRows = payload.total_rows || 0;
        state.paginate = payload.paginate || initialState.paginate;
      })
      .addCase(getTopicsWithoutProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error || action.payload || 'Failed to fetch topics';
        state.data = [];
      })

      // Create Topic
      .addCase(createTopic.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTopic.fulfilled, (state, action) => {
        const {data, message}  = action.payload || {};

        state.loading = false;
        if (action.payload && Array.isArray(state.data)) {
          state.data = [data.attributes, ...state.data];
          state.totalRows += 1;
          state.paginate = {
            ...state.paginate,
            total_rows: state.paginate.total_rows + 1
          };
        }
        toast.success(message);
      })
      .addCase(createTopic.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || 'Failed to create topic';
      })

      // Update Topic
      .addCase(updateTopic.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTopic.fulfilled, (state, action) => {
        const {data, message}  = action.payload || {};

        state.loading = false;
        if (action.payload && Array.isArray(state.data)) {
          const index = state.data.findIndex(topic => topic.id === data.id);
          if (index !== -1) {
            state.data[index] = data.attributes;
          }
        }
        toast.success(message);
      })
      .addCase(updateTopic.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || 'Failed to update topic';
      })

      // Delete Topic
      .addCase(deleteTopic.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTopic.fulfilled, (state, action) => {
        const {data, message}  = action.payload || {};

        state.loading = false;
        if (Array.isArray(state.data)) {
          state.data = state.data.filter(topic => topic.id !== data.id);
          state.totalRows = Math.max(0, state.totalRows - 1);
          state.paginate = {
            ...state.paginate,
            total_rows: Math.max(0, state.paginate.total_rows - 1)
          };
        }
        toast.success(message);
      })
      .addCase(deleteTopic.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || 'Failed to delete topic';
      })
      .addCase(closeTopic.fulfilled, (state, action) => {
        const {data, message}  = action.payload || {};
        
        state.loading = false;
        if (action.payload && Array.isArray(state.data)) {
          const index = state.data.findIndex(topic => topic.id === data.id);
          if (index !== -1) {
            state.data[index] = data.attributes;
          }
        }
        toast.success(message);
      })
      .addCase(closeTopic.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.error || 'Failed to close topic';
      })

      // Get Responses
      .addCase(getTopicResponses.pending, (state, action) => {
        const topicId = action.meta.arg?.topicId;
        if (topicId) {
          state.responseLoading = {
            ...state.responseLoading,
            [topicId]: true
          };
        }
      })
      .addCase(getTopicResponses.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const { topicId, rows = [], paginate = {} } = payload;
        
        if (topicId) {
          state.responseLoading = {
            ...state.responseLoading,
            [topicId]: false
          };
          
          const currentResponses = state.responses[topicId];
          const isFirstPage = (paginate.current_page || 1) === 1;
          const existingData = currentResponses?.data || [];
          
          state.responses = {
            ...state.responses,
            [topicId]: {
              data: isFirstPage 
                ? (Array.isArray(rows) ? rows : [])
                : appendPage(existingData, Array.isArray(rows) ? rows : []),
              visible: currentResponses?.visible ?? true,
              loaded: true,
              paginate: {
                total_rows: paginate.total_rows || 0,
                current_page: paginate.current_page || 1,
                prev_page: paginate.prev_page || null,
                next_page: paginate.next_page || null,
                per_page: paginate.per_page || 5,
                total_pages: paginate.total_pages || 1
              }
            }
          };
        }
      })
      .addCase(getTopicResponses.rejected, (state, action) => {
        const topicId = action.meta.arg?.topicId;
        if (topicId) {
          state.responseLoading = {
            ...state.responseLoading,
            [topicId]: false
          };
        }
        state.error = action.payload?.error || action.payload || 'Failed to fetch responses';
      })

      // Create Response
      .addCase(createResponse.pending, (state) => {
        state.error = null;
      })
      .addCase(createResponse.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const { topicId, response } = payload;
        
        if (topicId && response) {
          const currentResponses = state.responses[topicId];

          if (currentResponses && Array.isArray(currentResponses.data)) {
            state.responses = {
              ...state.responses,
              [topicId]: withCreatedResponse(currentResponses, response.data.attributes),
            };
          }

          // response_count nằm trên chính topic và chỉ được tính lại ở server.
          // Không cộng ở đây thì card vẫn hiện "0 responses" ngay bên trên danh
          // sách response vừa thêm, cho tới lần refetch danh sách kế tiếp.
          const topic = state.data.find((t) => t?.id === topicId);
          if (topic) topic.response_count = (topic.response_count || 0) + 1;
        }

        toast.success('Response added successfully');
      })
      .addCase(createResponse.rejected, (state, action) => {
        state.error = action.payload?.error || action.payload || 'Failed to add response';
      })

      .addCase(deleteResponse.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const { topicId, responseId } = payload;

        if (topicId && responseId !== undefined && state.responses[topicId]) {
          const currentResponses = state.responses[topicId];
          const responseData = currentResponses.data
          
          if (Array.isArray(responseData)) {
            const responseIndex = responseData.findIndex(r => r.id === responseId);
            if (responseIndex !== -1) {
              const updatedData = [...responseData];
              updatedData[responseIndex] = {
                ...updatedData[responseIndex],
              };
              
              state.responses = {
                ...state.responses,
                [topicId]: {
                  ...responseData,
                  data: updatedData
                }
              };

              toast.success('Response deleted successfully');
            }
          }
        }
      })

      // Like Response
      .addCase(likeResponse.fulfilled, (state, action) => {
        const payload = action.payload || {};
        const { topicId, responseId, likes } = payload;
        
        if (topicId && responseId !== undefined && state.responses[topicId]) {
          const currentResponses = state.responses[topicId];
          const responseData = currentResponses.data;
          
          if (Array.isArray(responseData)) {
            const responseIndex = responseData.findIndex(r => r.id === responseId);
            if (responseIndex !== -1) {
              const updatedData = [...responseData];
              updatedData[responseIndex] = {
                ...updatedData[responseIndex],
                attributes: {
                  ...updatedData[responseIndex].attributes,
                  likes
                }
              };
              
              state.responses = {
                ...state.responses,
                [topicId]: {
                  ...currentResponses,
                  data: updatedData
                }
              };
            }
          }
        }
      })
      .addCase(likeResponse.rejected, (state, action) => {
        state.error = action.payload?.error || action.payload || 'Failed to like response';
      })
      // Export price matrix
      .addCase(topicExportExcel.pending, (state) => {
        state.exporting = true;
      })
      .addCase(topicExportExcel.fulfilled, (state) => {
        state.exporting = false;
      })
      .addCase(topicExportExcel.rejected, (state) => {
        state.exporting = false;
      })

      // Add standard rejected action matcher using shared helper
      addRejectedMatcher(builder, "crm_project_interaction");
  }
});

export const {
  // setFilters,
  // setSortParams,
  setCurrentParams,
  clearError,
  resetState,
  onToggleResponses
} = interactionSlice.actions;

export default interactionSlice.reducer;